/**
 * SimulationProvider — admin "act as" (impersonation) state.
 *
 * When an admin simulates a user, we keep the admin's REAL Supabase session and
 * simply send `X-Act-As: <userId>` on API requests (via lib/api). The server —
 * which only honors the header for admins — scopes the effective identity + the
 * reveal gate to that user. The UI also renders with the simulated user's role
 * (effectiveRole) and shows an exit banner. Persisted in sessionStorage so a
 * refresh keeps the simulation; cleared on sign-out.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthProvider";
import { setActAsUserId } from "@/lib/api";
import type { AppRole } from "@/types";

export interface SimulatedUser {
  id: string;
  email: string;
  full_name: string | null;
  role: AppRole;
}

interface SimulationContextValue {
  simulatedUser: SimulatedUser | null;
  isSimulating: boolean;
  /** Role used for UI gating: the simulated role when simulating, else the real role. */
  effectiveRole: AppRole | null;
  simulate: (user: SimulatedUser) => void;
  stopSimulating: () => void;
}

const SimulationContext = createContext<SimulationContextValue | null>(null);
const STORAGE_KEY = "tkcc.simulate";

function readStored(): SimulatedUser | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SimulatedUser) : null;
  } catch {
    return null;
  }
}

export function SimulationProvider({ children }: { children: ReactNode }) {
  const { role: realRole } = useAuth();
  const queryClient = useQueryClient();
  const [simulatedUser, setSimulatedUser] = useState<SimulatedUser | null>(() => {
    const stored = readStored();
    // Restore the request-header sync before children make any API calls.
    if (stored) setActAsUserId(stored.id);
    return stored;
  });

  // Query keys carry no identity component, so entering/exiting a simulation
  // must drop the cache — otherwise the next render serves data fetched as the
  // OTHER identity until staleTime expires (breaking simulation fidelity).
  function simulate(user: SimulatedUser): void {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    setActAsUserId(user.id);
    setSimulatedUser(user);
    queryClient.clear();
  }

  function stopSimulating(): void {
    sessionStorage.removeItem(STORAGE_KEY);
    setActAsUserId(null);
    setSimulatedUser(null);
    queryClient.clear();
  }

  // If the admin signs out, drop any active simulation.
  useEffect(() => {
    if (!realRole && simulatedUser) stopSimulating();
  }, [realRole, simulatedUser]);

  const value = useMemo<SimulationContextValue>(
    () => ({
      simulatedUser,
      isSimulating: simulatedUser !== null,
      effectiveRole: simulatedUser?.role ?? realRole,
      simulate,
      stopSimulating,
    }),
    [simulatedUser, realRole],
  );

  return <SimulationContext.Provider value={value}>{children}</SimulationContext.Provider>;
}

export function useSimulation(): SimulationContextValue {
  const ctx = useContext(SimulationContext);
  if (!ctx) throw new Error("useSimulation must be used within <SimulationProvider>");
  return ctx;
}
